
import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import ReportTabContent from '@/components/compliance/reports/ReportTabContent';
import ReportHeader from '@/components/compliance/reports/ReportHeader';
import { useReportFilter } from '@/hooks/useReportFilter';
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

// Extended compliance reports with type field
const complianceReports = [
  {
    id: '1',
    name: 'Monthly Capital Report',
    frequency: 'Monthly',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '2',
    name: 'Annual Financial Statement',
    frequency: 'Annual',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '3',
    name: 'Capital Adequacy Return',
    frequency: 'Quarterly',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '4',
    name: 'Priority Sector Lending Return',
    frequency: 'Monthly',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '5',
    name: 'Non-Performing Assets Report',
    frequency: 'Quarterly',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '6',
    name: 'Statutory Liquidity Ratio',
    frequency: 'Fortnightly',
    applicable: true,
    type: 'RBI'
  },
  {
    id: '7',
    name: 'Financial Exposure Report',
    frequency: 'Quarterly',
    applicable: true,
    type: 'SEBI'
  },
  {
    id: '8',
    name: 'Investor Grievance Report',
    frequency: 'Monthly',
    applicable: true,
    type: 'SEBI'
  },
  {
    id: '9',
    name: 'Trading Volume Report',
    frequency: 'Monthly',
    applicable: true,
    type: 'Exchange'
  },
  {
    id: '10',
    name: 'Market Maker Performance',
    frequency: 'Quarterly',
    applicable: true,
    type: 'Exchange'
  },
  {
    id: '11',
    name: 'Investor Records Update',
    frequency: 'Monthly',
    applicable: true,
    type: 'RTA'
  },
  {
    id: '12',
    name: 'Dividend Distribution Report',
    frequency: 'Quarterly',
    applicable: true,
    type: 'RTA'
  },
];

const ReportTabSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="flex justify-between">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-10 w-32" />
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
);

const ComplianceReports: React.FC = () => {
  const { 
    searchTerm,
    setSearchTerm,
    selectedTab,
    setSelectedTab,
    handleTabChange,
    filteredReports
  } = useReportFilter(complianceReports);

  const [isLoading, setIsLoading] = useState(true);

  // Optimize with faster initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 50); // Reduced from 100ms to 50ms for more responsive feel
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-6">
      <ReportHeader />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full max-w-md" />
          <ReportTabSkeleton />
        </div>
      ) : (
        <Tabs defaultValue="RBI" value={selectedTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-4 mb-6">
            <TabsTrigger value="RBI">RBI Reports</TabsTrigger>
            <TabsTrigger value="SEBI">SEBI Reports</TabsTrigger>
            <TabsTrigger value="Exchange">Exchange Reports</TabsTrigger>
            <TabsTrigger value="RTA">RTA Reports</TabsTrigger>
          </TabsList>
          
          <TabsContent value={selectedTab} className="space-y-4">
            <LazyLoad delay={100}>
              <ReportTabContent 
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filteredReports={filteredReports}
                selectedTab={selectedTab}
              />
            </LazyLoad>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ComplianceReports;
